// webmd.ads.consumer.js
// DEPENDENCIES:
// - webmd.ads,
// - webmd.lotame
/*
 * Layer on top of webmd.ads or consumer websites.
 */

import { ua } from 'webmd-useragent';
import { isNewVisit } from 'webmd-visit';
import { getEnv, getLifecycle, getParam, getSLD } from 'webmd-url';
import { isAllowed } from 'webmd-pim';
import { getResponsiveWidthType } from 'webmd-core';
import ads from 'webmd-ads';
import debug from 'webmd-debug';
import cookie from 'webmd-cookie';
import { set as setAdsCookie } from 'webmd-ads-cookie';
import { get as getAdsCookie } from 'webmd-ads-cookie';


let adsConsumer;

if (typeof window !== 'undefined') {
  require('webmd-aam');
  require('webmd-medscape');

  // Create webmd object if it doesn't already exist
  window.webmd = window.webmd || {};

  adsConsumer = {

    /**
     * set by certain pages that dont want their Ad Target overwritten by the defaults in sensitive/sponsored/etc scenarios. e.g responsive daily video player.
     */
    skipDefaultAdUnits: false,

    /**
     * This function provides a single call for several things that the ad module must do.
     * It is provided as a shortcut to reduce the amount of javascript code that must
     * be dropped on the page from the ad module.
     *
     * @param Object settings
     * @param Object [settings.blockCodes]
     * @param Object settings.id
     * @param Object settings.pos
     * @param Object settings.sizes
     * @param Object [settings.targets]
     *
     */
    defineAd: function (settings) {

      // Check If ads=0 in query and return if true
      var url = (document.referrer) ? document.referrer : document.location.href;
      if ((url && url.indexOf('ads=0') > -1) || (window.globalConfig && window.globalConfig.ads && window.globalConfig.ads.noads)) {
        return;
      }
      window.__INITIAL_STATE__  = window.__INITIAL_STATE__  || {};
      window.__INITIAL_STATE__.pagedata = window.__INITIAL_STATE__.pagedata  || {ad:{
        blockCodes:'',
        targets:{}
      }};
      
      document.dispatchEvent(new CustomEvent('DEFINE_AD_TRIGGERED'));
      
      let adsAdminData = {};
      if (window.globalConfig && window.globalConfig.ads) {
        adsAdminData = window.globalConfig.ads;
      }

      if (adsAdminData.pageFlags && adsAdminData.pageFlags.adsPlacements) {
        if (adsAdminData.pageFlags.adsPlacements.id || adsAdminData.pageFlags.adsPlacements.pos) {
          if (adsAdminData.pageFlags.adsPlacements.id.includes(`${settings.id}`) || adsAdminData.pageFlags.adsPlacements.pos.includes(`${settings.pos}`)) {
            return;
          }
        }
      }

      if (window.globalConfig && window.globalConfig.ads && window.globalConfig.ads.pageFlags && window.globalConfig.ads.pageFlags.disabledDFPNative && window.globalConfig.ads.pageFlags.disabledDFPNative.disableDFP) {

        // If today on webmd disabled from admin tool
        if (window.globalConfig.ads.pageFlags.disabledDFPNative.today_on_webmd && settings.id.includes("dfpslot_tow")) {
          //to remove tow container when no default content is present
          const items = document.querySelectorAll('aside#ed-urr-2 div[id^=dfpslot_tow_2-] a');
          const displayValue = 'none';

          items.forEach((ele) => {

            if (ele.style.display != 'none')
              displayValue = 'block';

          });
          document.querySelector('aside#ed-urr-2').style.display = displayValue;
          return;
        }

      }

      let targets = {};
      if (window.__INITIAL_STATE__.pagedata && window.__INITIAL_STATE__.pagedata.ad && window.__INITIAL_STATE__.pagedata.ad.targets) {
        targets = Object.assign(targets, window.__INITIAL_STATE__.pagedata.ad.targets);
      }
      if (settings.targets) {
        targets = Object.assign(targets, settings.targets);
      }

      if (adsAdminData.adTargets) {
        targets = Object.assign(targets, adsAdminData.adTargets);
        this.removeSafeSelect();
      }

      // reset iuparts for while define ad call
      ads.setIuParts();
      this.removeSafeSelect();

      ads.googleDefer(() => {

        // Fix some of the targets because runtime sends some extraneous text that we don't want
        if (targets.env) {
          targets.env = ads.fixTarget('env', targets.env);
        } else {
          // PPE-73736: SET env in PROD ACTIVE to '0'. every other env is '1'
          targets.env = (!getEnv() && !getLifecycle()) ? '0' : '1';
        }
        if (targets.leaf) {
          targets.leaf = ads.fixTarget('leaf', targets.leaf);
        }
        if (targets.uri) {
          targets.uri = ads.fixTarget('uri', targets.uri);
        }
        if (targets.pt === '') {
          delete targets.pt;
        }
        if (targets.ex === '[exgid]') {
          delete targets.ex;
        }

        // Legacy functionality:
        // global variable webmd.adappid can be set to indicate the app value for all ad calls
        if (webmd.adappid) {
          targets.app = webmd.adappid;
        }

        /**
         * PPE-60843: Corrected format for PUG to DFP
         * Change the DFP code for PUGs as follows:
         * If the value is blank or if (as in CMS) it's two underscores "__" nothing should be sent to DFP
         * If the value is populated DFP code can send as-is. The format is already correct in these use cases on all Runtimes - CMS, O&O, Vits/Sups, Drugs.
         * Single value is just a number, such as "444444", Multiple values is sent as "4444,22222,11111"
         */
        if (targets.hasOwnProperty('pug')) {
          if (!targets.pug || targets.pug === '__') {
            delete targets.pug;
          }
        }

        //set default segment if none is defined
        if (!targets.segm) {
          targets.segm = '0';
        }

        // Check if any size is defined from defineAd function call
        if (this.sizeIsNotDefined(settings.sizes)) {
          // provide a default size if no size is defined
          settings.sizes = this.getSizeForPos(settings.pos);
        }

        // add additional ad sizes to existing list of sizes if sizes are not configured in admin tool
        if (window.globalConfig && window.globalConfig.ads) {

          const { adPosConfig } = window.globalConfig.ads;
          if (adPosConfig && adPosConfig[settings.pos] && adPosConfig[settings.pos].sizes && adPosConfig[settings.pos].sizes.length > 0) {
            settings.sizes = adPosConfig[settings.pos].sizes;
          } else {
            settings.sizes = this.addSizes(settings.pos, settings.sizes);
          }

        } else {
          settings.sizes = this.addSizes(settings.pos, settings.sizes);
        }

        //PPE-509468: Removing sizes [640, 520], [785, 570] from iPhone  and Android devices for POS 5000
        //1000px is set as a limit so that the sizes wont be removed for Android tablets in landscape orientation
        if (ua.device === 'iphone' || ua.device === 'android' && settings.pos && settings.pos == '5000' && window.innerWidth && window.innerWidth < 1000) {
          settings.sizes = settings.sizes.filter(size => {
            return !(Array.isArray(size) && ((size[0] === 640 && size[1] === 520) || (size[0] === 785 && size[1] === 570)));
          });
        }

        // Set ad tag emb=1 to indicate if the current page is an embedded asset
        if (typeof window.ads_emb !== 'undefined' && window.ads_emb === '1') {
          targets.emb = '1';
        }
        this.setResponsive(targets);

        // PPE-88551: We should observe lineItemId passed as 0 on the first ad call
        this.setTargets(targets); // pass targetted value in ad call (lif, saf)

        // Set the page targets
        ads.setPageTarget(targets);

        // Set default slot level targets
        this.setSlotLevelTargets(settings);

        // Set the block codes
        if(window.__INITIAL_STATE__ && window.__INITIAL_STATE__.pagedata && window.__INITIAL_STATE__.pagedata.ad && window.__INITIAL_STATE__.pagedata.ad.blockCodes) {
          ads.setPageExclusionUnderscore(window.__INITIAL_STATE__.pagedata.ad.blockCodes);
        }
        // Check if safe-select needs to be triggered
        this.checkRules();

        if (window.s_sponsor_program === '' && !window.noBt && window.adBlocker && !(ads.adTarget[ads.adTarget.length - 1].indexOf('-block')>-1)) {
          
          ads.adTarget[ads.adTarget.length - 1] += '-block';

          // set the page level target upbidder
          ads.setPageTarget("up_bidder", "true");

        }

        // Define the ad
        ads.defineAd(settings);

      });
    },

    /**
     * This function should be called at end of the page to display the DFP ads.
     */
    display: function () {

      let cmt, usprivacycookie;


      ads.googleDefer(() => {

        // Check if PIM maximum has been reached
        // (or if something else on the page has already claimed the PIM interrupt)
        // Note this is also done at the top of the page but we repeat it at the bottom.
        if (!isAllowed()) {
          ads.setPageTarget('pimx', '1');
        }

        // set 'pch' - for google unified pricing- PPE-264185 (original implementation -PPE-206379)
        // value must be between 1 and 4
        ads.setPageTarget('pch', Math.floor(Math.random() * 4) + 1);

        // Pass Lotame ID stored in a cooke to DFP - https://jira.webmd.net/browse/PPE-121501
        if (cookie('lpid') && !cookie('WBMD_AUTH')) {
          ads.setPageTarget('lpid', cookie('lpid'));
        }

        // Pass nlid cookie value to DFP
        var nlidCookieValue = cookie.get('nlsub');
        if (nlidCookieValue && nlidCookieValue.length > 0) {
          ads.setPageTarget('nlsub', nlidCookieValue);
        }

				// Patient intercept check for vitals and care pages
        if( window.location.href.indexOf('doctor.') > -1 || window.location.href.indexOf('.vitals') > -1){
					let initial_state = window.__INITIAL_STATE__;
					if ( initial_state && initial_state.profile && initial_state.profile.profiletype && initial_state.profile.profiletype == 'Basic' ) {
          	ads.setPageTarget('pi', '1');
					} else {
          	ads.setPageTarget('pi', '0');
					}
				}

        // Capture cmt campaign values from cmt cookie value
        // Split the values and pass to 'cmt' in ad call if it is 3 digit
        // else pass it to 'ml' in ad call
        cmt = cookie.get('cmt');
        if (cmt) {
          try {
            cmt = JSON.parse(cmt);
            if (typeof cmt.campaigns !== 'undefined') {
              var cmtData = [], mlData = [];
              for (var i = 0; i < cmt.campaigns.length; i++) {
                if (cmt.campaigns[i].length === 3) {
                  cmtData.push(cmt.campaigns[i]);
                } else {
                  mlData.push(cmt.campaigns[i]);
                }
              }
              ads.setPageTarget('cmt', cmtData.join(','));
              ads.setPageTarget('ml', mlData.join(','));
            }
          } catch (e) {
            debug("Something went wrong while trying to parse cmt cookie.\n", e);
          }
        }

        // get usprivacy cookie value and pass it to 'usps' as page level target value
        // pass 3rd character of the cookie value to 'usoo' as page level target value
        usprivacycookie = cookie.get('usprivacy');
        if (usprivacycookie) {
          try {
            var usoo = usprivacycookie.charAt(2);
            ads.setPageTarget('usps', usprivacycookie);
            ads.setPageTarget('usoo', usoo);
          } catch (e) {
            webmd.debug('Something went wrong while fetching the usprivacy cookie', e);
          }
        }

        //check for referrer and if it is google pass ref = 1 as page level target
        if (document.referrer && document.referrer.includes('https://www.google.')) {
          ads.setPageTarget('ref', 1);
        }
        // check for ads following cookie and pass it in ad call
        let sponsorProgram = window.s_sponsor_program || "";
        if (getParam('pi') && sponsorProgram !== "") {
          this.setAdsFollowing(sponsorProgram);
        } else {
          this.setAdsFollowing();
        }

        //Pass cbp kvp to ad call
        if (sponsorProgram && sponsorProgram !== "") {
          ads.setPageTarget('cbp', sponsorProgram);
        }

        this.addLocationData();

        ads.display();
      });
    },

    hasStorage: function () {
      try {
        sessionStorage.setItem('test', '1');
        sessionStorage.removeItem('test');
        return true;
      } catch (e) {
        return false;
      }
    },

    setSafeSelect: function () {
      ads.setPageExclusion('ssg');
    },
    removeSafeSelect: function () {
      ads.removePageExclusion('ssg');
      ads.removePageExclusion('0');
    },

    // IU parts are defined as a folder hierarchy. For example "4312434/consumer/webmd".
    // Some applications like local health directory, are defined as sub-folders of a parent app;
    // for example "4312434/consumer/webmd/dirconwbmd".
    // since we can potentially have several apps defined as subfolders of a parent app, we can represent the
    // hierarchy with the following data structure.
    setUserGeneratedContent: function () {

      let adUnit2, map, setUgc;

      map = {
        webmd: ['webmdugc', { "dir-conwbmd": "dir-wbmdugc" }],
        mednet: ['mednetugc'],
        rxlist: ['rxlistugc'],
        emed: ['emedugc'],
        onhealth: ['onhealthugc'],
        webmdmobileweb: ['webmdmobilewebugc', { "dir-cmbwwbmd": "dir-wbmdmwugc" }],
        mednetmobileweb: ['mednetmobilewebugc'],
        rxlistmobileweb: ['rxlistmobilewebugc'],
        emedmobileweb: ['emedmobilewebugc'],
        onhealthmobileweb: ['onhealthmobilewebugc'],
        vitals: ['vitalsugc'],
        vitalsmobileweb: ['vitalsmwugc']
      };

      // modifies the last bit of the iu_parts
      // [consugc, webmdugc,dir-conwbmd] becomes [consugc, webmdugc,dir-wbmdugc]
      setUgc = function (pivot) {
        Object.keys(map).forEach((item) => {
          if (map[item][0] === pivot && typeof map[item][1] !== 'undefined') {
            let obj = map[item][1];
            let key = ads.adTarget[2];

            if (typeof obj[key] !== 'undefined') {
              ads.adTarget[2] = obj[key];
            }
          }
        });
      };

      // Set ad unit 2 based on the mapping.
      // Note: since this function could potentially run multiple times
      // we can't just append "ugc" onto the end.
      adUnit2 = ads.adTarget[1];
      adUnit2 = map[adUnit2] || adUnit2;

      if (Array.isArray(adUnit2)) {
        ads.adTarget[1] = adUnit2[0];
      } else ads.adTarget[1] = adUnit2;

      if (ads.adTarget.length > 2) { // i.e [consumer,webmd,dir-conwbmd]
        setUgc(ads.adTarget[1]);
      }

      // Set ad unit 1 to consugc or consugcmobileweb based on desktop vs mobile
      ads.adTarget[0] = (ads.adTarget[0].indexOf('mobile') > 0) ? 'consugcmobileweb' : 'consugc';

      // Also turn on "ssg"
      this.setSafeSelect();
    },

    setSensitive: function () {
      let map;

      // PPE-105363 skipDefaultAdUnits is set by certain pages that dont want their Ad Target overwritten by the default code below. e.g responsive daily video player.
      if (!this.skipDefaultAdUnits) {
        // Ad unit 3 must be set to a special value based on ad unit 2,
        // so create an object to map the correct value
        map = {
          webmd: 'st-conwbmd',
          webmdmobileweb: 'st-cmbwwbmd',
          mednet: 'st-conmnet',
          mednetmobileweb: 'st-cmbwmnet',
          rxlist: 'st-conrxl',
          rxlistmobileweb: 'st-cmbwrxl',
          emed: 'st-conemed',
          emedmobileweb: 'st-cmbwemed',
          onhealth: 'st-cononh',
          onhealthmobileweb: 'st-cmbwonh',
          vitals: 'st-vitals',
          vitalsmobileweb: 'st-vitalsmobileweb'
        };

        // set adunit 3 to indicate sensitive topic. ( excluding care pages )
        if (!(window.s_pagename && window.s_pagename.indexOf('doctor.') > -1)) {
          ads.adTarget[2] = map[ads.adTarget[1]] || '';
        }
      }
      // Also turn on "ssg"
      this.setSafeSelect();
    },

    setHomepage: function () {

      let map;

      // Ad unit 3 must be set to a special value based on ad unit 2,
      // so create an object to map the correct value
      map = {
        webmd: 'hp-conwbmd',
        webmdmobileweb: 'hp-cmbwwbmd',
        mednet: 'hp-conmnet',
        mednetmobileweb: 'hp-cmbwmnet',
        rxlist: 'hp-conrxl',
        rxlistmobileweb: 'hp-cmbwrxl',
        emed: 'hp-conemed',
        emedmobileweb: 'hp-cmbwemed',
        onhealth: 'hp-cononh',
        onhealthmobileweb: 'hp-cmbwonh',
        vitals: 'st-vitals',
        vitalsmobileweb: 'st-vitalsmobileweb'
      };

      // Set ad unit 3 to indicate we are on a homepage ( excluding care pages )
      if (!(window.s_pagename && window.s_pagename.indexOf('doctor.') > -1)) {
        ads.adTarget[2] = map[ads.adTarget[1]] || '';
      }

      // Also turn on "ssg"
      this.setSafeSelect();
    },

    setSponsored: function () {
      let map;

      // PPE-105363+PPE-120631 skipDefaultAdUnits is set by certain pages that dont want their Ad Target overwritten by the default code below. e.g responsive daily video player.
      if (!this.skipDefaultAdUnits) {
        // Ad unit 3 must be set to a special value based on ad unit 2,
        // so create an object to map the correct value
        map = {
          webmd: 'sp-conwbmd',
          webmdmobileweb: 'sp-cmbwwbmd',
          mednet: 'sp-conmnet',
          mednetmobileweb: 'sp-cmbwmnet',
          rxlist: 'sp-conrxl',
          rxlistmobileweb: 'sp-cmbwrxl',
          emed: 'sp-conemed',
          emedmobileweb: 'sp-cmbwemed',
          onhealth: 'sp-cononh',
          onhealthmobileweb: 'sp-cmbwonh',
          vitals: 'sp-vitals',
          vitalsmobileweb: 'sp-vitalsmobileweb'
        };

        // set adunit 3 to indicate sponsored page. ( excluding care pages )
        if (!(window.s_pagename && window.s_pagename.indexOf('doctor.') > -1)) {
          ads.adTarget[2] = map[ads.adTarget[1]] || '';
        }
      }

      // Also turn on "ssg"
      this.setSafeSelect();
    },

    /**
     * Add default key/value pair to slot level targets
     * @param Object settings - settings from defineAd 
     * 
    */
    setSlotLevelTargets: function (settings) {
      settings.keys = settings.keys || {}
      // Adding up the slot level targets from admin tool data
      if (window.globalConfig && window.globalConfig.ads) {
        const { adPosConfig } = window.globalConfig.ads;
        if (adPosConfig && adPosConfig[settings.pos] && adPosConfig[settings.pos].slotLevelTargets) {
          settings.keys = Object.assign(settings.keys, adPosConfig[settings.pos].slotLevelTargets);
        }
      }
      // map with the pos value from settings and assign default keys
      settings.keys.ad_slot = settings.id;
      if (this.rules.slotTargets[settings.pos]) {
        settings.keys = Object.assign(settings.keys, this.rules.slotTargets[settings.pos]);
      }
    },

    /*
     * add key/pair values to ad targets
     * @param target Object - object representing key/pair values
     * @returns Object
     PPE-88551, PPE-90003, PPE-92440, PPE-92430, PPE-172510
     only get from sessionStorage. cookie->session is only one-time when page-init.
    */
    setTargets: function (targets) {
      if (this.hasStorage()) {
        let clif, clif_obj;
        try {
          clif_obj = webmd.adsCookie.get('sess').lif; //Object {111798914: "lif111798914x"}
          if (clif_obj) {
            clif = Object.keys(clif_obj)[Object.keys(clif_obj).length - 1];
            targets.lif = clif || 0;
          } else {
            targets.lif = 0;
          }
        }
        catch (e) {
        }

        let csaf, csaf_obj;
        try {
          csaf_obj = webmd.adsCookie.get('sess').saf; //Object {4845995429: "saf4845995429x"}
          if (csaf_obj) {
            csaf = Object.keys(csaf_obj)[Object.keys(csaf_obj).length - 1];
            targets.saf = csaf || 0;
          } else {
            targets.saf = 0;
          }
        }
        catch (e) {
        }
      }
      return targets;
    },

    /**
     * PPE-61965: Identify Responsive Breakpoints to DFP
     * Width 1240+ / bp=1
     * Width 1023 - 1239 / bp=2
     * Width 768 - 1019 / bp=3
     * Width 640 - 767 / bp=4
     * Width <640 / bp=5
     * Also add parameter rd=1
     */
    setResponsive: function (targets) {

      targets = targets || {};
      targets.bp = getResponsiveWidthType();

      return targets;
    },



    /**
     * Check various rules for safe select, user generated content, sensitive topics,
     * sponsorted destinations, and home pages.
     */
    checkRules: function () {

      let exclusions, rules, targets;

      exclusions = ads.pageExclusions;
      rules = this.rules;
      targets = ads.pageTargets;

      debug(targets);

      // set adunit 3 for care pages.
      // special case for LHD: see https://jira.webmd.net/browse/PPE-339961
      if (window.s_pagename && window.s_pagename.indexOf('doctor.') > -1) {
        if (ua.type === 'mobile') {
          ads.adTarget[2] = 'dir-cmbwwbmd';
        } else {
          ads.adTarget[2] = 'dir-conwbmd';
        }
      }

      // Check for user-generated content.
      if (rules.ugcApp[targets.app] ||
        rules.ugcCc[targets.cc] ||
        rules.ugcPt[targets.pt]) {
        debug("UGC");
        this.setUserGeneratedContent();
      }

      // Check for sensitive topics
      if (rules.sensitiveTopic[targets.pt] ||
        rules.sensitiveTopic[targets.sec] ||
        rules.sensitiveCc[targets.cc]) {
        debug("Sensitive");
        this.setSensitive();
      }

      // Check for safe-select
      if (!rules.adUnit1[ads.adTarget[0]] ||
        !rules.adUnit2[ads.adTarget[1]] ||
        (targets.app && !rules.app[targets.app]) ||
        rules.cc[targets.cc] ||
        rules.pt[targets.pt] ||
        exclusions["0"]) {
        debug("Safe Select");
        this.setSafeSelect();
      }

      // Check for sponsorship
      if (window.s_sponsor_program ||
        rules.sponsoredPt[targets.pt] ||
        targets.tug) {
        debug("Sponsored");
        this.setSponsored();
      }

      // Check for homepage
      if (rules.homepageCc[targets.cc] ||
        rules.homepagePt[targets.pt]) {
        debug("Homepage");
        this.setHomepage();
      }

      /**
       * special case for Blogs: see https://jira.webmd.net/browse/PPE-369274
       * The iuParts for blogs pages are set from pagebuilder. i.e [consugc, webmdugc] 
       * Blogs pages fall under the content class 148.
       * Overriding the adunit from pagebuilder and setting the required ad unit 
       */
      if ((window.center_prefix && window.center_prefix.indexOf('blogs') > -1)) {
        if (ua.type === 'mobile') {
          ads.adTarget = ['consmobileweb', 'webmdmobileweb', 'webmdblogsmobileweb'];
        } else {
          ads.adTarget = ['consumer', 'webmd', 'webmdblogsdesktop'];
        }
      }

      /**
       * Overriding iuParts with admin tool data: see https://jira.internetbrands.com/browse/PPE-408108
       * The iuParts for webmd pages are fetched from ads admin tool through webmd-cms-store.
       * The iuParts fetched from admin tool should take precedence over the iuParts from ads code. 
       * Overriding the iuParts from ads code with admin tool data.
       */
      if (window.globalConfig && window.globalConfig.ads) {
        const { iuParts } = window.globalConfig.ads;
        if (ua.type === 'mobile' && iuParts && iuParts.mobile && iuParts.mobile.length > 1) {
          ads.adTarget = iuParts.mobile;
        } else if (ua.type !== 'mobile' && iuParts && iuParts.desktop && iuParts.desktop.length > 1) {
          ads.adTarget = iuParts.desktop;
        }
      }

    },

    //return default size per position provided
    getSizeForPos: function (posId) {

      if (this.rules.defaultSizes[parseInt(posId, 0)]) {
        //return the default size array
        return this.rules.defaultSizes[parseInt(posId, 0)];
      }
      else {
        throw new Error('no position or default size defined');
      }
    },

    //return true is size is not defined
    sizeIsNotDefined: function (sizesValue) {

      //if size value is defined, is an array and is populated
      if (sizesValue && Array.isArray(sizesValue) && sizesValue.length >= 1) {
        return false;
      }

      return true;
    },

    /*
     * adds a default size to the existing array of sizes
     * @param {String} pos - pos for an ad slot
     * @param {Array} sizes - An array of sizes for the ad slot
     * @returns {Array} sizes array with the default size added to it
     */
    addSizes: function (pos, sizes) {

      if (parseInt(pos, 0) in this.rules.additionalSizesToSet) {
        if (sizes.length) { // make sure sizes array is not empty
          if (!Array.isArray(sizes[0])) { // make sure it's a 2D array
            // if it's a 1D array, turn it into a 2D array and then push the fluid size into it
            sizes = [sizes];
          }
          // if the size for the pos is an array then push each into 'sizes'
          if (Array.isArray(this.rules.additionalSizesToSet[parseInt(pos, 0)])) {
            // if the size for the pos is array then looping it to push into sizes
            this.rules.additionalSizesToSet[parseInt(pos, 0)].forEach((size) => {
              // avoid adding sizes that are already present in the sizes array
              if (sizes.toString().indexOf(size.toString()) === -1) {
                sizes.push(size);
              }
            });
          } else if (this.rules.additionalSizesToSet[parseInt(pos, 0)] === 'fluid') {
            // if its string only then push it directly into sizes
            sizes.push(this.rules.additionalSizesToSet[parseInt(pos, 0)]);
          }
        }
      }

      return sizes;
    },

    /*
     * adds the location codes to the ad call as page level targets
     *
     * @returns {Bool} true if targets were added, false if they don't exist in the cookie
     */
    addLocationData: function () {
      let gtinfo = cookie.get('gtinfo'),
        codes = { cntr: 'dcou', ctc: 'dctc', dma: 'ddma', st: 'dst', z: 'dzip' };
      // putting gtinfo !== 'undefined' because sometimes when the cookie dropped as undefined, it will come as a string observed in lower environments https://jira.internetbrands.com/browse/PPE-329793
      if (gtinfo && gtinfo !== "" && gtinfo !== 'undefined') {
        gtinfo = JSON.parse(gtinfo);
      } else {
        return false;
      }

      Object.keys(codes).forEach((val) => {
        if (typeof gtinfo[val] !== 'undefined') {
          ads.setPageTarget(codes[val], gtinfo[val]);
        }
      });

      return true;
    },
    /**
     * sets the ads following functionality on priority basis, value can be served dynamically.
     *
     * @param  target {string} - the cbp/any other value passed dynamically
     *
     * @returns null
     * @example ads.setAdsFollowing('CBP');
     * @description First priority is parameter,
     * if the target is not available then go for cookie and pass it to ads call on
     * every page load, if the cookie isavailable
     */
    setAdsFollowing: function (target) {
      var sess = getAdsCookie('sess');
      // If no cookie and target available, return
      if (!sess && !target) {
        debug('No cookie or target available for ads following');
        return;
      }
      if (target) {
        ads.setPageTarget('dsf', target);
        setAdsCookie({
          type: 'sess',
          prefix: 'ds',
          value: 'sp',
          count: target
        });
      } else if (sess && sess.ds && sess.ds.sp && typeof sess.ds.sp === 'string') {
        var matches = sess.ds.sp.match(/^dssp_(.+)x$/);
        if (window.s_sponsor_program && window.s_sponsor_program != undefined && matches && matches[1] !== window.s_sponsor_program) {
          setAdsCookie({
            type: 'sess',
            prefix: 'ds',
            value: 'sp',
            count: window.s_sponsor_program
          });
          ads.setPageTarget('dsf', window.s_sponsor_program);

        } else if (matches) {
          ads.setPageTarget('dsf', matches[1]);
        }
      }
    },

    /**
     * Rules that will be used to determine when to set various flags
     */
    rules: {

      // Ad unit 1 values (previously known as "site") that ALLOW safe select
      adUnit1: {
        'consumer': true,
        'consmobileweb': true,
        'consuk': true,
        'consukmobileweb': true
      },

      // Ad unit 2 values (previously known as "affiliate") that ALLOW safe select
      adUnit2: {
        'webmd': true,
        'webmdmobileweb': true,
        'mednet': true,
        'mednetmobileweb': true,
        'rxlist': true,
        'rxlistmobileweb': true,
        'emed': true,
        'emedmobileweb': true,
        'onhealth': true,
        'onhealthmobileweb': true,
        'boots': true,
        'bootsmobileweb': true
      },

      // App ids that ALLOW safe select
      // Any other app id (if set) will block safe select
      app: {
        7: true, // Drug Database FDA
        8: true, // Drug Database FDB
        18: true, // V&S Database Reference Content
        22: true, // O&O Slideshow and RMQ
        27: true, // MedicineNet Drug Monographs
        28: true // EMH Drug Monographs
        // 29:true // EMH Multum Monographs
      },

      // Content classifications that must be blocked
      cc: {
        52: true, // Tool – Assessment
        53: true, // Tool – Calculator
        54: true, // Tool – Animation
        55: true, // Tool – Quiz
        84: true, // Search: Nav - Medline Search
        85: true, // Search: Nav - Recipe Finder
        86: true, // Search: Nav - Search
        87: true, // Search: Nav - Site Search
        90: true, // Search: Nav - Web Search
        99: true, // Tool - Am I a Candidate
        93: true, // Symptom Checker
        100: true, // Tool – Form
        111: true, // Tool – Drugs
        112: true, // Tool – Game
        113: true, // Tool
        121: true, // Search: Nav - Drug Search
        122: true, // Search: Nav - Vitamins Search
        127: true, // Search: Index
        128: true, // Search: Index – Drugs
        129: true, // Search: Index – Vitamins
        131: true, // Tool – Viewer
        138: true, // Tool – Action Plan
        139: true // Tool – Article Player
      },

      // Leaf ids to block
      leaf: {
        1141: true, // symptom checker
        6141: true, // symptom checker
        13404: true // symptom checker
      },

      // Primary topic that must be blocked
      pt: {
        3644: true, // Branded Recipe
        4117: true, // symptom checker page 1
        4112: true, // symptom checker page 2
        4113: true, // symptom checker page 3
        4121: true // Sponsor Content
      },

      // App ids for user generated content that must be blocked
      ugcApp: {
        2: true, // Message Boards
        4: true, // Blogs
        5: true, // Physician Finder
        12: true, // Drug Database UGC
        17: true, // Blog User Comments - Psych Today
        19: true, // V&S Database UGC
        21: true, // O&O - UGC
        23: true, // WebMD Answers
        200: true, // WebMD Exchange - WebMD Hosted
        201: true, // WebMD Exchange - Member Hosted
        202: true, // WebMD Exchange - Nav pages
        203: true, // WebMD Exchange - Moderated A to Z
        204: true, // WebMD Exchange - Experts A to Z
        500: true // Drugs.com - UGC
      },

      // Content classifications for user generated content
      ugcCc: {
        109: true, // Drug Review
        110: true // Drug Review Add
      },

      // Primiary topic for user generated content
      ugcPt: {
        3552: true, // Exchanges
        3562: true // Chronic Disease and Disability (for Exchanges only)
      },

      // Primary or secondary topics that should be marked as sensitive topics
      // TODO: can be just block all 7000 range topic ids? Also can we assume 8000 range are sponsored?
      sensitiveTopic: {
        4050: true, // (Product Recall)
        7000: true, // (Abortion)
        7001: true, // (Child Abuse)
        7002: true, // (Addiction & Substance Abuse)
        7004: true, // (Childhood Genetic Diseases)
        7005: true, // (Domestic Abuse)
        7006: true, // (Eating Disorders)
        7007: true, // (Grief & Death)
        7009: true, // (Miscarriage)
        7010: true, // (Pregnancy Complications)
        7012: true, // (Sexual Abuse)
        7013: true, // (Suicide)
        7014: true, // (Trauma)
        7015: true, // (Violence)
        7019: true, // (embargo ID)
        7021: true, // (Body Image Issues)
        7022: true, // (Binge Eating Disorder)
        7023: true, // (Anorexia)
        7024: true, // (Bulimia)
        7025: true, // (CoronaVirus)
        7121: true, // (Sensitive Sponsor)
        7122: true // (Palliative Care)
      },

      sensitiveCc: {
        20: true, // Product recall
        61: true // Drug recall
      },

      sponsoredPt: {
        3644: true, // Branded Recipe
        4121: true // Sponsor Content
      },

      homepageCc: {
        83: true // Nav - Homepage
      },

      homepagePt: {
        1728: true, // Home and Top Stories
        home: true // O&O sites seem to use primary topic "home" for home page
      },

      //default ad sizes per pos
      defaultSizes: {
        101: [728, 90],
        102: [1, 15],
        103: [728, 90],
        105: [970, 90],
        108: [728, 90],
        111: [120, 240],
        112: [120, 600],
        113: [160, 600],
        114: [300, 250],
        115: [160, 600],
        121: [300, 250],
        131: [300, 600],
        132: [300, 600],
        133: [300, 600],
        137: [300, 250],
        138: [3, 4],
        141: [728, 90],
        404: [300, 250],
        409: [300, 250],
        700: [300, 198],
        701: [145, 199],
        901: [1, 1],
        902: [1, 1],
        903: [1, 4],
        920: [1, 6],
        921: [1, 7],
        922: [1, 8],
        923: [9, 1],
        926: [2, 6],
        1380: [3, 4],
        1901: [1, 1],
        1903: [1, 4],
        1920: [1, 6],
        1921: [1, 7],
        1922: [1, 8],
        1923: [9, 1],
        1926: [2, 6],
        1111: [300, 50],
        2108: [320, 50],
        5000: [494, 1],
        5001: [494, 1],
        5002: [308, 1],
        5003: [308, 1],
        5004: [4, 1],
        5054: [4, 1],
        5100: [1, 3],
        2025: [300, 50],
        2026: [300, 50],
        2051: [1, 15],
        109: [728, 90],
        2109: [300, 250]

      },

      /*
       * default ad sizes per pos value. This ad size will always be sent along in the ad call
       * This effectively adds the 'fluid' size to every pos defined in this object.
       * Sizes can be string for fluid or array of sizes like [[1,9],[9,2]]
       */
      additionalSizesToSet: {
        111: ['fluid', [728, 90]],
        1111: ['fluid', [320, 50]],
        141: ['fluid', [1, 9], [1, 16], [728, 92]],
        921: 'fluid',
        922: [[970, 251]],
        138: 'fluid',
        1380: 'fluid',
        923: ['fluid', [1, 9], [1, 16], [300, 250]],
        1921: 'fluid',
        1922: [[300, 601]],
        1923: ['fluid', [1, 9], [300, 250], [1, 16], [300, 256]],
        1926: [[300, 250]],
        2026: ['fluid', [320, 180], [320, 250], [320, 320], [410, 418], [1, 16], [300, 51], [300, 250], [300, 253], [320, 50], [320, 51], [300, 256]],
        5000: ['fluid', [640, 480], [640, 520], [785, 570], [1, 2]],
        5001: [[1, 2]],
        5002: [[1, 2]],
        5003: [[1, 2]],
        5004: 'fluid',
        5054: 'fluid',
        137: ['fluid', [300, 251], [300, 600]],
        101: ['fluid', [970, 90]],
        102: 'fluid',
        114: [[300, 255]],
        121: ['fluid', [1, 15], [300, 251], [300, 600], [300, 1050]],
        409: [300, 252],
        2025: ['fluid', [320, 50], [1, 17]],
        2108: 'fluid',
        2109: ['fluid', [1, 13]],
        109: ['fluid', [1, 13]],
        2051: 'fluid',
        1926: [[300, 250]]
      },
      /**
       * These are default slot level targets for specific pos values
       * key value pairs which are aded here will be sent in ad call prev_scp section 
       */
      slotTargets: {
        2026: { fbid: "385978254785998_2217983684918770", strnativekey: "JP2PR9q6cJCLZtj36uN7K5cd" },
        141: { strnativekey: "kEMWZGoo8vzvtwJKrcPEDvnd" },
        923: { strnativekey: "Vjx59uEm8EVWkMyP8f8smvF8" },
        1923: { strnativekey: "Vjx59uEm8EVWkMyP8f8smvF8" },
        921: { strnativekey: "xP8Dby8p66dX1vZpKKVKX4fu" },
        1921: { strnativekey: "xP8Dby8p66dX1vZpKKVKX4fu" }
      }
    }
  };

  window.webmd = window.webmd || {};

  window.webmd.adsConsumer = adsConsumer;
}

export default adsConsumer;
